import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faUsers, faCalendarAlt, faInfoCircle, faHistory, faFileAlt, faSpinner, faBuilding, faEnvelope, faEye, faChevronLeft, faChevronRight, faPlus, faEdit, faTrash, faTasks } from '@fortawesome/free-solid-svg-icons';
import { Tab, Tabs, TabList, TabPanel } from 'react-tabs';
import { workingGroupsService } from '../../services/workingGroupsService';
import 'react-tabs/style/react-tabs.css';
import PropTypes from 'prop-types';
import './WorkingGroupDetailsModal.css';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { toast } from 'react-toastify';
import DocumentDetailsModal from '../Repository/DocumentDetailsModal';
import Tippy from '@tippyjs/react';
import 'tippy.js/dist/tippy.css';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { formatDateForInput, countWords, formatDate, formatDateSimple, cleanLinks, removeURLs, removeFirstBoldTag } from '../../utils/textUtils';
import apiHelper from '../../utils/apiHelper';

// Add stripHtml function
const stripHtml = (html) => {
    if (!html) return '';
    const tmp = document.createElement('DIV');
    tmp.innerHTML = html;
    return tmp.textContent || tmp.innerText || '';
};

const WorkingGroupDetailsModal = ({ workingGroup, onHide, onUpdate }) => {
    const [members, setMembers] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [membersPerPage] = useState(6); // Show 6 members per page (2 rows of 3)
    const [searchTerm, setSearchTerm] = useState('');
    const [showAddMemberModal, setShowAddMemberModal] = useState(false);
    const [editingMember, setEditingMember] = useState(null);
    const [newMember, setNewMember] = useState({
        FirstName: '',
        LastName: '',
        WorkingGroupPosition: '',
        CompanyName: '',
        MemberStatus: 'Live',  // Default value
        WorkingGroupName: workingGroup.WorkingGroupName, // Pre-filled from current group
        WorkingGroupType: workingGroup.WorkingGroupType,
        SteeringGroupType: 'N/a',
        SteeringGroupName: 'N/a',
        StartDate: new Date().getFullYear().toString(),
        EndDate: (new Date().getFullYear() + 2).toString(), // Default 2 years from now
        MemberCohort: ''
    });
    const [isEditing, setIsEditing] = useState(false);
    const [editedGroup, setEditedGroup] = useState(null);
    const [documents, setDocuments] = useState([]);
    const [isLoadingDocs, setIsLoadingDocs] = useState(false);
    const [currentDocsPage, setCurrentDocsPage] = useState(1);
    const [docsPerPage] = useState(6); // Show 6 documents per page
    const [searchDocsTerm, setSearchDocsTerm] = useState('');
    const [selectedDocument, setSelectedDocument] = useState(null);
    const [updates, setUpdates] = useState([]);
    const [isLoadingUpdates, setIsLoadingUpdates] = useState(false);
    const [showAddUpdateModal, setShowAddUpdateModal] = useState(false);
    const [newUpdate, setNewUpdate] = useState({
        updateDate: new Date().toISOString().split('T')[0],
        update: '',
        updatedBy: ''
    });
    const [editingUpdate, setEditingUpdate] = useState(null);
    const [showEditUpdateModal, setShowEditUpdateModal] = useState(false);
    const [torHeader, setTorHeader] = useState('');
    const [torObjectives, setTorObjectives] = useState([]);
    const [torData, setTorData] = useState([]);
    const [isEditingHeader, setIsEditingHeader] = useState(false);
    const [editingObjective, setEditingObjective] = useState(null);
    const [showAddObjectiveModal, setShowAddObjectiveModal] = useState(false);
    const [newObjective, setNewObjective] = useState('');

    // Add this function near the top of your component to safely render HTML
    const createMarkup = (htmlContent) => {
        return { __dangerouslySetInnerHTML: { __html: htmlContent } };
    };

    // Add these near your other const declarations
    const formats = [
        'bold', 'italic',
        'list', 'bullet'
    ];

    const modules = {
        toolbar: [
            ['bold', 'italic'],
            [{ 'list': 'ordered'}, { 'list': 'bullet' }]
        ]
    };

    useEffect(() => {
        const fetchMembers = async () => {
            setIsLoading(true);
            setError(null);
            try {
                const data = await workingGroupsService.getWorkingGroupMembers(workingGroup.WorkingGroupName);
                setMembers(data);
            } catch (err) {
                setError('Failed to load members');
                console.error('Error loading members:', err);
            } finally {
                setIsLoading(false);
            }
        };

        fetchMembers();
    }, [workingGroup.WorkingGroupName]);

    useEffect(() => {
        if (workingGroup) {
            setEditedGroup({
                Title: workingGroup.Title,
                WorkingGroupName: workingGroup.WorkingGroupName,
                WorkingGroupType: workingGroup.WorkingGroupType,
                SteeringGroupName: workingGroup.SteeringGroupName,
                SteeringGroupType: workingGroup.SteeringGroupType,
                StartDate0: workingGroup.StartDate0,
                EndDate0: workingGroup.EndDate0,
                PublishFlag: workingGroup.PublishFlag
            });
        }
    }, [workingGroup]);

    useEffect(() => {
        const fetchDocuments = async () => {
            setIsLoadingDocs(true);
            try {
                const response = await workingGroupsService.getWorkingGroupDocuments(workingGroup.id);
                setDocuments(response);
              
            } catch (error) {
                console.error('Error fetching documents:', error);
                toast.error('Failed to load documents');
            } finally {
                setIsLoadingDocs(false);
            }
        };

        fetchDocuments();
    }, [workingGroup.id]);

    // Add this helper function to check if an update is valid
    const isValidUpdate = (update) => {
        return update && (update.update || update.Update_x003a_Nov24);
    };

    // Modify the useEffect to filter out invalid updates
    useEffect(() => {
        const fetchUpdates = async () => {
            setIsLoadingUpdates(true);
            try {
                const response = await workingGroupsService.getWorkingGroupUpdates(workingGroup.id);
                const validUpdates = response.filter(isValidUpdate);
                setUpdates(validUpdates);
            } catch (error) {
                console.error('Error fetching updates:', error);
                toast.error('Failed to load updates');
                setUpdates([]);
            } finally {
                setIsLoadingUpdates(false);
            }
        };

        fetchUpdates();
    }, [workingGroup.id]);

    // Filter members based on search term before pagination
    const filteredMembers = members.filter(member => {
        const searchLower = searchTerm.toLowerCase();
        return (
            `${member.fields.FirstName} ${member.fields.LastName}`.toLowerCase().includes(searchLower) ||
            member.fields.CompanyName?.toLowerCase().includes(searchLower) ||
            member.fields.WorkingGroupPosition?.toLowerCase().includes(searchLower)
        );
    });

    // Update pagination to use filtered members
    const indexOfLastMember = currentPage * membersPerPage;
    const indexOfFirstMember = indexOfLastMember - membersPerPage;
    const currentMembers = filteredMembers.slice(indexOfFirstMember, indexOfLastMember);

    // Reset to first page when search changes
    useEffect(() => {
        setCurrentPage(1);
    }, [searchTerm]);

    // Change page
    const paginate = (pageNumber) => setCurrentPage(pageNumber);

    // Calculate total pages
    const totalPages = Math.ceil(filteredMembers.length / membersPerPage);

    const handleAddMember = () => {
        setShowAddMemberModal(true);
    };

    const handleEditMember = (member) => {
        setEditingMember({
            id: member.id,
            FirstName: member.fields.FirstName,
            LastName: member.fields.LastName,
            WorkingGroupPosition: member.fields.WorkingGroupPosition,
            CompanyName: member.fields.CompanyName,
            MemberStatus: member.fields.MemberStatus,
            WorkingGroupName: member.fields.WorkingGroupName,
            WorkingGroupType: member.fields.WorkingGroupType,
            SteeringGroupType: member.fields.SteeringGroupType,
            SteeringGroupName: member.fields.SteeringGroupName,
            StartDate: member.fields.StartDate,
            EndDate: member.fields.EndDate,
            MemberCohort: member.fields.MemberCohort
        });
        setShowAddMemberModal(true);  // Reuse the same modal
    };

    const handleSaveMember = async () => {
        try {
            const memberData = editingMember || newMember;
            
            if (!memberData.FirstName || !memberData.LastName || !memberData.WorkingGroupPosition || !memberData.CompanyName) {
                alert('Please fill in all required fields');
                return;
            }

            if (editingMember) {
                // Update existing member
                await workingGroupsService.updateWorkingGroupMember(editingMember.id, memberData);
            } else {
                // Create new member
                await workingGroupsService.createWorkingGroupMember(memberData);
            }
            
            // Refresh the members list
            const updatedMembers = await workingGroupsService.getWorkingGroupMembers(workingGroup.WorkingGroupName);
            setMembers(updatedMembers);
            
            // Reset form and close modal
            setShowAddMemberModal(false);
            setEditingMember(null);
            setNewMember({
                FirstName: '',
                LastName: '',
                WorkingGroupPosition: '',
                CompanyName: '',
                MemberStatus: 'Live',
                WorkingGroupName: workingGroup.WorkingGroupName,
                WorkingGroupType: workingGroup.WorkingGroupType,
                SteeringGroupType: 'N/a',
                SteeringGroupName: 'N/a',
                StartDate: new Date().getFullYear().toString(),
                EndDate: (new Date().getFullYear() + 2).toString(),
                MemberCohort: ''
            });

            toast.success('Member saved successfully! 🎉');
            
        } catch (error) {
            console.error('Error saving member:', error);
            toast.error('Failed to save member 😕');
        }
    };

    const handleSave = async () => {
        if (editedGroup.WorkingGroupName !== workingGroup.WorkingGroupName) {
            if (!window.confirm(
                "⚠️ Warning: Changing the Working Group name may affect other connected lists and data.\n\n" +
                "Are you sure you want to proceed with this change?"
            )) {
                return;
            }
        }

        try {
            await workingGroupsService.updateWorkingGroup(workingGroup.id, editedGroup);
            await onUpdate();
            toast.success('Working group updated successfully! 🎯');
            onHide();
        } catch (error) {
            console.error('Error updating working group:', error);
            toast.error('Failed to update working group');
        }
    };

    // Modify the handleSaveUpdate function
    const handleSaveUpdate = async () => {
        const wordCount = countWords(newUpdate.update);
        if (wordCount > 150) {
            toast.error('Update content exceeds 150 words limit. Please shorten your text.');
            return;
        }

        try {
            await workingGroupsService.createWorkingGroupUpdate({
                ...newUpdate,
                workingGroupNameId: workingGroup.id
            });
            
            // Refresh updates
            const response = await workingGroupsService.getWorkingGroupUpdates(workingGroup.id);
            setUpdates(response);
            
            // Reset form and close modal
            setNewUpdate({
                updateDate: new Date().toISOString().split('T')[0],
                update: '',
                updatedBy: ''
            });
            setShowAddUpdateModal(false);
            toast.success('Update added successfully! 🎉');
            
        } catch (error) {
            console.error('Error saving update:', error);
            toast.error('Failed to save update');
        }
    };

    // When opening the edit modal, format the date
    const handleEditClick = (update) => {
        setEditingUpdate({
            ...update,
            updateDate: formatDateForInput(update.updateDate)  // Format the date here
        });
        setShowEditUpdateModal(true);
    };

    // Modify the handleEditUpdate function
    const handleEditUpdate = async () => {
        const wordCount = countWords(editingUpdate.update);
        if (wordCount > 150) {
            toast.error('Update content exceeds 150 words limit. Please shorten your text.');
            return;
        }

        try {
            if (!editingUpdate) return;

            await workingGroupsService.updateWorkingGroupUpdate(editingUpdate.id, {
                updateDate: editingUpdate.updateDate,
                update: editingUpdate.update,
                updatedBy: editingUpdate.updatedBy
            });
            
            // Refresh updates
            const response = await workingGroupsService.getWorkingGroupUpdates(workingGroup.id);
            setUpdates(response);
            
            // Reset form and close modal
            setEditingUpdate(null);
            setShowEditUpdateModal(false);
            toast.success('Update modified successfully! 🎉');
            
        } catch (error) {
            console.error('Error updating update:', error);
            toast.error('Failed to modify update');
        }
    };

    const handleDeleteUpdate = async (updateId) => {
        if (window.confirm('Are you sure you want to delete this update? This action cannot be undone.')) {
            try {
                await workingGroupsService.deleteWorkingGroupUpdate(updateId);
                
                // Remove the deleted update from the state
                const updatedUpdates = updates.filter(update => update.id !== updateId);
                setUpdates(updatedUpdates);
                
                toast.success('Update deleted successfully! 🗑️');
                
            } catch (error) {
                console.error('Error deleting update:', error);
                toast.error('Failed to delete update');
            }
        }
    };

    const renderAddMemberModal = () => {
        return (
            <>
                {/* Backdrop */}
                {showAddMemberModal && (
                    <div 
                        className="modal-backdrop show"
                        onClick={() => setShowAddMemberModal(false)}
                    ></div>
                )}
                
                {/* Modal */}
                <div 
                    className={`modal ${showAddMemberModal ? 'd-block show' : ''}`} 
                    tabIndex="-1"
                    role="dialog"
                >
                    <div 
                        className="modal-dialog"
                        onClick={e => e.stopPropagation()}
                    >
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">{editingMember ? 'Edit Member' : 'Add New Member'}</h5>
                                <button type="button" className="btn-close" onClick={() => setShowAddMemberModal(false)}></button>
                            </div>
                            <div className="modal-body">
                                <form>
                                    <div className="mb-3">
                                        <label className="form-label">First Name *</label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            value={editingMember ? editingMember.FirstName : newMember.FirstName}
                                            onChange={(e) => {
                                                if (editingMember) {
                                                    setEditingMember({...editingMember, FirstName: e.target.value});
                                                } else {
                                                    setNewMember({...newMember, FirstName: e.target.value});
                                                }
                                            }}
                                        />
                                    </div>
                                    <div className="mb-3">
                                        <label className="form-label">Last Name *</label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            value={editingMember ? editingMember.LastName : newMember.LastName}
                                            onChange={(e) => {
                                                if (editingMember) {
                                                    setEditingMember({...editingMember, LastName: e.target.value});
                                                } else {
                                                    setNewMember({...newMember, LastName: e.target.value});
                                                }
                                            }}
                                        />
                                    </div>
                                    <div className="mb-3">
                                        <label className="form-label">Position *</label>
                                        <select 
                                            className="form-select"
                                            value={editingMember ? editingMember.WorkingGroupPosition : newMember.WorkingGroupPosition}
                                            onChange={(e) => {
                                                if (editingMember) {
                                                    setEditingMember({...editingMember, WorkingGroupPosition: e.target.value});
                                                } else {
                                                    setNewMember({...newMember, WorkingGroupPosition: e.target.value});
                                                }
                                            }}
                                        >
                                            <option value="">Select Position</option>
                                            <option value="Chair">Chair</option>
                                            <option value="Vice Chair">Vice Chair</option>
                                            <option value="Member">Member</option>
                                        </select>
                                    </div>
                                    <div className="mb-3">
                                        <label className="form-label">Company *</label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            value={editingMember ? editingMember.CompanyName : newMember.CompanyName}
                                            onChange={(e) => {
                                                if (editingMember) {
                                                    setEditingMember({...editingMember, CompanyName: e.target.value});
                                                } else {
                                                    setNewMember({...newMember, CompanyName: e.target.value});
                                                }
                                            }}
                                        />
                                    </div>
                                    <div className="mb-3">
                                        <label className="form-label">Member Cohort</label>
                                        <select 
                                            className="form-select"
                                            value={editingMember ? editingMember.MemberCohort : newMember.MemberCohort}
                                            onChange={(e) => {
                                                if (editingMember) {
                                                    setEditingMember({...editingMember, MemberCohort: e.target.value});
                                                } else {
                                                    setNewMember({...newMember, MemberCohort: e.target.value});
                                                }
                                            }}
                                        >
                                            <option value="">Select Cohort</option>
                                            <option value="Auditor">Auditor</option>
                                            <option value="Legal">Legal</option>
                                            <option value="Member Firm">Member Firm</option>
                                        </select>
                                    </div>
                                </form>
                            </div>
                            <div className="modal-footer">
                                <button type="button" className="btn btn-secondary" onClick={() => setShowAddMemberModal(false)}>
                                    Cancel
                                </button>
                                <button type="button" className="btn btn-primary" onClick={handleSaveMember}>
                                    Save Member
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </>
        );
    };

    const renderMembers = () => {
        // Let's make sure the Add Member button is outside any conditional rendering
        return (
            <div className="members-section">
                {/* Add Member button - ALWAYS VISIBLE */}
                <div className="d-flex justify-content-end mb-3">
                    <button 
                        className="btn btn-primary" 
                        onClick={() => handleAddMember()}
                    >
                        <i className="fas fa-plus"></i> Add Member
                    </button>
                </div>

                {/* Then the rest of the members content */}
                {members.length > 0 ? (
                    <>
                        <div className="input-group mb-3">
                            <input
                                type="text"
                                className="form-control"
                                placeholder="Search members..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                            {searchTerm && (
                                <button 
                                    className="btn btn-outline-secondary" 
                                    onClick={() => setSearchTerm('')}
                                >
                                    Clear
                                </button>
                            )}
                        </div>

                        <div className="members-grid">
                            {currentMembers.map(member => (
                                <div key={member.id} className="member-card">
                                    <div className="d-flex justify-content-between align-items-start">
                                        <div>
                                            <div className="member-name">
                                                {member.fields.FirstName} {member.fields.LastName}
                                            </div>
                                            <div className="member-position">
                                                {member.fields.WorkingGroupPosition}
                                            </div>
                                            <div className="member-company">
                                                {member.fields.CompanyName}
                                            </div>
                                        </div>
                                        <button 
                                            className="btn btn-sm btn-outline-primary"
                                            onClick={() => handleEditMember(member)}
                                        >
                                            <i className="fas fa-edit"></i>
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </>
                ) : (
                    <div className="text-center text-muted mt-4">
                        <p>No members found. Add your first member!</p>
                    </div>
                )}
                
                {/* Pagination */}
                {totalPages > 1 && (
                    <div className="pagination-container">
                        <div className="pagination">
                            <button 
                                className="btn btn-outline-primary" 
                                onClick={() => paginate(currentPage - 1)}
                                disabled={currentPage === 1}
                            >
                                Previous
                            </button>
                            
                            {[...Array(totalPages)].map((_, index) => (
                                <button
                                    key={index + 1}
                                    className={`btn ${currentPage === index + 1 ? 'btn-primary' : 'btn-outline-primary'}`}
                                    onClick={() => paginate(index + 1)}
                                >
                                    {index + 1}
                                </button>
                            ))}
                            
                            <button 
                                className="btn btn-outline-primary" 
                                onClick={() => paginate(currentPage + 1)}
                                disabled={currentPage === totalPages}
                            >
                                Next
                            </button>
                        </div>
                    </div>
                )}
            </div>
        );
    };

    const renderDetailsTab = () => {
        if (isEditing) {
            return (
                <form>
                    <div className="mb-3">
                        <label className="form-label">Working Group Name *</label>
                        <input
                            type="text"
                            className="form-control"
                            value={editedGroup.WorkingGroupName}
                            onChange={(e) => setEditedGroup({
                                ...editedGroup,
                                WorkingGroupName: e.target.value
                            })}
                        />
                    </div>
                    <div className="mb-3">
                        <label className="form-label">Working Group Type *</label>
                        <select 
                            className="form-select"
                            value={editedGroup.WorkingGroupType}
                            onChange={(e) => setEditedGroup({
                                ...editedGroup,
                                WorkingGroupType: e.target.value
                            })}
                        >
                            <option value="">Select Type</option>
                            <option value="Specialist Group">Specialist Group</option>
                            <option value="Steering Group">Steering Group</option>
                        </select>
                    </div>
                    <div className="mb-3">
                        <label className="form-label">Start Date</label>
                        <input
                            type="text"
                            className="form-control"
                            value={editedGroup.StartDate0}
                            onChange={(e) => setEditedGroup({
                                ...editedGroup,
                                StartDate0: e.target.value
                            })}
                        />
                    </div>
                    <div className="mb-3">
                        <label className="form-label">End Date</label>
                        <input
                            type="text"
                            className="form-control"
                            value={editedGroup.EndDate0}
                            onChange={(e) => setEditedGroup({
                                ...editedGroup,
                                EndDate0: e.target.value
                            })}
                        />
                    </div>
               
                </form>
            );
        }

        // Regular view mode
        return (
            <div>
                <div className="d-flex justify-content-end mb-3">
                    <button 
                        className="btn btn-primary" 
                        onClick={() => setIsEditing(true)}
                    >
                        <i className="fas fa-edit"></i> Edit
                    </button>
                </div>
                <dl className="row">
                    <dt className="col-sm-4">Working Group Name</dt>
                    <dd className="col-sm-8">{workingGroup.WorkingGroupName}</dd>
                    
                    <dt className="col-sm-4">Working Group Type</dt>
                    <dd className="col-sm-8">{workingGroup.WorkingGroupType}</dd>
                    
                    <dt className="col-sm-4">Start Date</dt>
                    <dd className="col-sm-8">{workingGroup.StartDate0}</dd>
                    
                    <dt className="col-sm-4">End Date</dt>
                    <dd className="col-sm-8">{workingGroup.EndDate0}</dd>
                    
                    {/* Add other fields as needed */}
                </dl>
            </div>
        );
    };

    // Update your modal footer to include Save/Cancel when editing
    const renderModalFooter = () => (
        <>
            {isEditing && (
                <div className="modal-footer">
                    <button 
                        className="btn btn-secondary" 
                        onClick={() => setIsEditing(false)}
                    >
                        Cancel
                    </button>
                    <button 
                        className="btn btn-primary" 
                        onClick={handleSave}
                    >
                        Save Changes
                    </button>
                </div>
            )}
        </>
    );

    // Filter documents based on search term
    const filteredDocuments = documents?.documents?.filter(doc => {
        if (!searchDocsTerm) return true;
        const searchLower = searchDocsTerm.toLowerCase();
        return (
            doc.title.toLowerCase().includes(searchLower) ||
            doc.type?.toLowerCase().includes(searchLower) ||
            doc.fileName?.toLowerCase().includes(searchLower)
        );
    }) || [];

    // Get current documents for pagination
    const indexOfLastDoc = currentDocsPage * docsPerPage;
    const indexOfFirstDoc = indexOfLastDoc - docsPerPage;
    const currentDocuments = filteredDocuments.slice(indexOfFirstDoc, indexOfLastDoc);

    // Calculate total pages for documents
    const totalDocsPages = Math.ceil((filteredDocuments.length || 0) / docsPerPage);

    // Change documents page
    const paginateDocs = (pageNumber) => setCurrentDocsPage(pageNumber);

    // Reset documents page when search changes
    useEffect(() => {
        setCurrentDocsPage(1);
    }, [searchDocsTerm]);

    const formatMetadata = (doc) => {
        const parts = [];
        
        if (doc.type) parts.push(`📄 ${doc.type}`);
        if (doc.month) parts.push(`📅 ${doc.month}`);
        if (doc.year) parts.push(`${doc.year}`);
        if (doc.qtr) parts.push(`Q${doc.qtr}`);
        
        return parts.join(' • ') || 'No metadata';
    };

    const renderTooltipContent = (doc) => (
        <div className="document-tooltip">
            <h3>{doc.title}</h3>
            {doc.summary && (
                <p className="document-summary">{doc.summary}</p>
            )}
            <div className="document-tooltip-meta">
                <span>{new Date(doc.modified).toLocaleDateString('en-GB', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                })}</span>
                <span>{formatMetadata(doc)}</span>
            </div>
        </div>
    );

    useEffect(() => {
        const fetchTorData = async () => {
            try {
               
                const response = await workingGroupsService.getWorkingGroupTOR(workingGroup.id);
               
                setTorData(response);
                
                // Access category and description from the fields object
                const header = response.find(tor => tor.fields?.Category === "Header");
                const objectives = response.filter(tor => tor.fields?.Category === "Objective");
                
                
                setTorHeader(header?.fields?.TORDescription || '');
                setTorObjectives(objectives.map(obj => ({
                    id: obj.id,
                    torDescription: obj.fields?.TORDescription || ''
                })) || []);
            } catch (error) {
                console.error('Error fetching TOR:', error);
                toast.error('Failed to load Terms of Reference');
            }
        };

        fetchTorData();
    }, [workingGroup.id]);

    const handleSaveHeader = async () => {
        try {
 
            // Find the header item to get its ID
            const headerItem = torData.find(tor => tor.fields?.Category === "Header");

            if (!headerItem) {
                throw new Error('Header not found');
            }
            
            await workingGroupsService.updateWorkingGroupTORHeader({
                itemId: headerItem.id,
                torDescription: torHeader
            });
            
            setIsEditingHeader(false);
            toast.success('Header updated successfully! 🎯');
            
        } catch (error) {
            console.error('Error saving header:', error);
            console.error('Error details:', {
                message: error.message,
                response: error.response?.data,
                status: error.response?.status
            });
            toast.error('Failed to save header');
        }
    };

    const handleEditObjective = (objective) => {
        setEditingObjective(objective);
        setNewObjective(objective.torDescription);
        setShowAddObjectiveModal(true);
    };

    const handleDeleteObjective = async (objectiveId) => {
        if (window.confirm('Are you sure you want to delete this objective? This action cannot be undone.')) {
            try {
                await workingGroupsService.deleteWorkingGroupTORObjective(objectiveId);
                
                // Update local state
                setTorObjectives(prevObjectives => 
                    prevObjectives.filter(obj => obj.id !== objectiveId)
                );
                
                toast.success('Objective deleted successfully! 🗑️');
                
            } catch (error) {
                console.error('Error deleting objective:', error);
                toast.error('Failed to delete objective');
            }
        }
    };

    const handleSaveObjective = async () => {
        try {
            if (!newObjective.trim()) {
                toast.error('Objective content cannot be empty');
                return;
            }

            const objectiveData = {
                workingGroupId: workingGroup.id,
                torDescription: newObjective
            };

            if (editingObjective) {
                // Update existing objective
                await workingGroupsService.updateWorkingGroupTORObjective(
                    editingObjective.id,
                    objectiveData
                );
                
                // Update local state
                setTorObjectives(prevObjectives =>
                    prevObjectives.map(obj =>
                        obj.id === editingObjective.id
                            ? { ...obj, torDescription: newObjective }
                            : obj
                    )
                );
            } else {
                // Create new objective
                const response = await workingGroupsService.createWorkingGroupTORObjective(objectiveData);
                
                // Update local state with the new objective
                setTorObjectives(prevObjectives => [...prevObjectives, {
                    id: response.id,
                    torDescription: newObjective
                }]);
            }

            // Reset form and close modal
            setNewObjective('');
            setEditingObjective(null);
            setShowAddObjectiveModal(false);
            toast.success(`Objective ${editingObjective ? 'updated' : 'added'} successfully! 🎉`);
            
        } catch (error) {
            console.error('Error saving objective:', error);
            console.error('Error details:', {
                message: error.message,
                response: error.response?.data,
                status: error.response?.status
            });
            toast.error('Failed to save objective');
        }
    };

    return (
        <div className="modal-overlay" onClick={onHide}>
            <div className="working-group-details-modal" onClick={e => e.stopPropagation()}>
                <div className="modal-header">
                    <h5 className="modal-title">Working Group Details</h5>
                    <div className="modal-actions">
                        <button 
                            type="button" 
                            className="close-button" 
                            onClick={onHide}
                            aria-label="Close"
                        >
                            <FontAwesomeIcon icon={faTimes} />
                        </button>
                    </div>
                </div>

                <div className="modal-body">
                    <Tabs>
                        <TabList>
                            <Tab>
                                <FontAwesomeIcon icon={faInfoCircle} />
                                Overview
                            </Tab>
                            <Tab>
                                <FontAwesomeIcon icon={faUsers} />
                                Members ({members.length})
                            </Tab>
                            <Tab>
                                <FontAwesomeIcon icon={faHistory} />
                                Updates ({updates.filter(isValidUpdate).length})
                            </Tab>
                            <Tab>
                                <FontAwesomeIcon icon={faFileAlt} />
                                Documents ({documents?.documents?.length || 0})
                            </Tab>
                            <Tab>
                                <FontAwesomeIcon icon={faTasks} />
                                TOR
                            </Tab>
                        </TabList>

                        {/* Overview Tab */}
                        <TabPanel>
                            <div className="tab-content">
                                {/* Basic Info Section */}
                                <div className="details-section">
                                    <h3>Group Information</h3>
                                    {renderDetailsTab()}
                                </div>

                                {/* Steering Group Section */}
                                <div className="details-section">
                                    <h3>Steering Group Information</h3>
                                    <div className="info-grid">
                                        <div className="info-item">
                                            <label>Name</label>
                                            <div className="value">{workingGroup.SteeringGroupName}</div>
                                        </div>
                                        <div className="info-item">
                                            <label>Type</label>
                                            <div className="value">{workingGroup.SteeringGroupType}</div>
                                        </div>
                                    </div>
                                </div>

                                {/* Dates Section */}
                                <div className="details-section">
                                    <h3>Timeline</h3>
                                    <div className="info-grid">
                                        <div className="info-item">
                                            <label>Start Date</label>
                                            <div className="value">
                                                <FontAwesomeIcon icon={faCalendarAlt} className="icon-margin" />
                                                {workingGroup.StartDate0}
                                            </div>
                                        </div>
                                        <div className="info-item">
                                            <label>End Date</label>
                                            <div className="value">
                                                <FontAwesomeIcon icon={faCalendarAlt} className="icon-margin" />
                                                {workingGroup.EndDate0}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </TabPanel>

                        {/* Members Tab */}
                        <TabPanel>
                            <div className="tab-content">
                                <div className="details-section">
                                    <h3>Members ({members.length})</h3>
                                    {renderMembers()}
                                </div>
                            </div>
                        </TabPanel>

                        {/* Updates Tab */}
                        <TabPanel>
                            <div className="tab-content">
                                <div className="details-section">
                                    <div className="d-flex justify-content-between align-items-center mb-4">
                                        <h3>Updates</h3>
                                        <button 
                                            className={`btn ${updates.length === 0 ? 'btn-primary' : 'btn-outline-primary'}`}
                                            onClick={() => setShowAddUpdateModal(true)}
                                        >
                                            <FontAwesomeIcon icon={faPlus} /> Add Update
                                        </button>
                                    </div>
                                    
                                    {isLoadingUpdates ? (
                                        <div className="loading-state">
                                            <FontAwesomeIcon icon={faSpinner} spin />
                                            <span>Loading updates...</span>
                                        </div>
                                    ) : updates.length > 0 ? (
                                        <div className="updates-list">
                                            {updates.map(update => (
                                                <div key={update.id} className="update-card">
                                                    <div className="update-header">
                                                        <div className="update-date">
                                                            {new Date(update.updateDate).toLocaleDateString('en-GB', {
                                                                year: 'numeric',
                                                                month: 'long',
                                                                day: 'numeric'
                                                            })}
                                                        </div>
                                                        <div className="d-flex align-items-center gap-2">
                                                            <div className="update-author">
                                                                By: {update.updatedBy}
                                                            </div>
                                                            <div className="update-actions">
                                                                <button 
                                                                    className="btn btn-sm btn-outline-primary"
                                                                    onClick={() => handleEditClick(update)}
                                                                >
                                                                    <FontAwesomeIcon icon={faEdit} />
                                                                </button>
                                                                <button 
                                                                    className="btn btn-sm btn-outline-danger"
                                                                    onClick={() => handleDeleteUpdate(update.id)}
                                                                >
                                                                    <FontAwesomeIcon icon={faTrash} />
                                                                </button>
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <div 
                                                        className="update-content"
                                                        dangerouslySetInnerHTML={{ 
                                                            __html: update.update || update.Update_x003a_Nov24 || 'No content available'
                                                        }}
                                                    />
                                                </div>
                                            ))}
                                        </div>
                                    ) : (
                                        <div className="empty-state text-center py-5">
                                            <FontAwesomeIcon 
                                                icon={faHistory} 
                                                size="3x" 
                                                className="text-muted mb-3"
                                            />
                                            <h4>No Updates Yet</h4>
                                            
                                            
                                        </div>
                                    )}
                                </div>
                            </div>
                        </TabPanel>

                        {/* Documents Tab */}
                        <TabPanel>
                            <div className="tab-content">
                                <div className="details-section">
                                    <h3>Documents ({documents?.documents?.length || 0})</h3>
                                    {isLoadingDocs ? (
                                        <div className="loading-state">
                                            <FontAwesomeIcon icon={faSpinner} spin />
                                            <span>Loading documents...</span>
                                        </div>
                                    ) : documents?.documents?.length > 0 ? (
                                        <>
                                            {/* Search Documents */}
                                            <div className="input-group mb-3">
                                                <input
                                                    type="text"
                                                    className="form-control"
                                                    placeholder="Search documents..."
                                                    value={searchDocsTerm}
                                                    onChange={(e) => setSearchDocsTerm(e.target.value)}
                                                />
                                                {searchDocsTerm && (
                                                    <button 
                                                        className="btn btn-outline-secondary" 
                                                        onClick={() => setSearchDocsTerm('')}
                                                    >
                                                        Clear
                                                    </button>
                                                )}
                                            </div>

                                            {/* Documents List */}
                                            <div className="documents-list">
                                                <table className="table table-hover">
                                                    <thead>
                                                        <tr>
                                                            <th>Document Name</th>
                                                            <th>Type</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {currentDocuments.map(doc => (
                                                            <tr 
                                                                key={doc.id}
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    setSelectedDocument(doc);
                                                                }}
                                                                style={{ cursor: 'pointer' }}
                                                            >
                                                                <td>
                                                                    <Tippy
                                                                        content={renderTooltipContent(doc)}
                                                                        placement="right"
                                                                        duration={[200, 0]}
                                                                        delay={[500, 0]}
                                                                        interactive={true}
                                                                        maxWidth={400}
                                                                        theme="dark"
                                                                        appendTo={() => document.body}
                                                                    >
                                                                        <span>{doc.title}</span>
                                                                    </Tippy>
                                                                </td>
                                                                <td>{doc.type}</td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            </div>

                                            {/* Documents Pagination */}
                                            {totalDocsPages > 1 && (
                                                <div className="pagination-container mt-3">
                                                    <nav aria-label="Documents pagination">
                                                        <ul className="pagination pagination-sm">
                                                            <li className={`page-item ${currentDocsPage === 1 ? 'disabled' : ''}`}>
                                                                <button 
                                                                    className="page-link" 
                                                                    onClick={() => paginateDocs(currentDocsPage - 1)}
                                                                    disabled={currentDocsPage === 1}
                                                                >
                                                                    <FontAwesomeIcon icon={faChevronLeft} />
                                                                </button>
                                                            </li>
                                                            
                                                            <li className="page-item active">
                                                                <span className="page-link">
                                                                    Page {currentDocsPage} of {totalDocsPages}
                                                                </span>
                                                            </li>
                                                            
                                                            <li className={`page-item ${currentDocsPage === totalDocsPages ? 'disabled' : ''}`}>
                                                                <button 
                                                                    className="page-link" 
                                                                    onClick={() => paginateDocs(currentDocsPage + 1)}
                                                                    disabled={currentDocsPage === totalDocsPages}
                                                                >
                                                                    <FontAwesomeIcon icon={faChevronRight} />
                                                                </button>
                                                            </li>
                                                        </ul>
                                                    </nav>
                                                </div>
                                            )}
                                        </>
                                    ) : (
                                        <div className="empty-state">
                                            <p>No documents available for this working group.</p>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </TabPanel>

                        {/* TOR Tab */}
                        <TabPanel>
                            <div className="tab-content">
                                <div className="details-section">
                                    <div className="d-flex justify-content-between align-items-center mb-4">
                                        <h3>Terms of Reference</h3>
                                    </div>

                                    {/* Header Section */}
                                    <div className="tor-header-section mb-4">
                                        <div className="d-flex justify-content-between align-items-center">
                                            <h4>Header</h4>
                                            {!isEditingHeader ? (
                                                <button 
                                                    className="btn btn-outline-primary btn-sm"
                                                    onClick={() => setIsEditingHeader(true)}
                                                >
                                                    <FontAwesomeIcon icon={faEdit} /> Edit Header
                                                </button>
                                            ) : (
                                                <div className="btn-group">
                                                    <button 
                                                        className="btn btn-outline-secondary btn-sm"
                                                        onClick={() => setIsEditingHeader(false)}
                                                    >
                                                        Cancel
                                                    </button>
                                                    <button 
                                                        className="btn btn-primary btn-sm"
                                                        onClick={handleSaveHeader}
                                                    >
                                                        Save
                                                    </button>
                                                </div>
                                            )}
                                        </div>
                                        {isEditingHeader ? (
                                            <ReactQuill
                                                theme="snow"
                                                value={torHeader}
                                                onChange={setTorHeader}
                                                modules={modules}
                                                formats={formats}
                                                className="mt-3"
                                            />
                                        ) : (
                                            <div 
                                                className="tor-content mt-3"
                                                dangerouslySetInnerHTML={{ __html: torHeader || 'No header defined' }}
                                            />
                                        )}
                                    </div>

                                    {/* Objectives Section */}
                                    <div className="tor-objectives-section">
                                        <div className="d-flex justify-content-between align-items-center mb-3">
                                            <h4>Objectives</h4>
                                            <button 
                                                className="btn btn-outline-primary btn-sm"
                                                onClick={() => setShowAddObjectiveModal(true)}
                                            >
                                                <FontAwesomeIcon icon={faPlus} /> Add Objective
                                            </button>
                                        </div>
                                        <div className="objectives-list">
                                            {torObjectives.map((objective, index) => (
                                                <div key={objective.id} className="objective-card">
                                                    <div className="objective-content">
                                                        <div className="objective-number">{index + 1}</div>
                                                        <div 
                                                            className="objective-text"
                                                            dangerouslySetInnerHTML={{ __html: objective.torDescription }}
                                                        />
                                                    </div>
                                                    <div className="objective-actions">
                                                        <button 
                                                            className="btn btn-sm btn-outline-primary"
                                                            onClick={() => handleEditObjective(objective)}
                                                        >
                                                            <FontAwesomeIcon icon={faEdit} />
                                                        </button>
                                                        <button 
                                                            className="btn btn-sm btn-outline-danger"
                                                            onClick={() => handleDeleteObjective(objective.id)}
                                                        >
                                                            <FontAwesomeIcon icon={faTrash} />
                                                        </button>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </TabPanel>
                    </Tabs>
                </div>

                {renderModalFooter()}
            </div>

            {/* Add Member Modal */}
            {renderAddMemberModal()}

            {showAddUpdateModal && (
                <>
                    <div className="modal-backdrop show" onClick={() => setShowAddUpdateModal(false)}></div>
                    <div className="modal d-block show" tabIndex="-1" role="dialog">
                        <div className="modal-dialog modal-lg modal-dialog-centered" onClick={e => e.stopPropagation()}>
                            <div className="modal-content" style={{ maxHeight: '75vh', overflowY: 'auto' }}>
                                <div className="modal-header">
                                    <h5 className="modal-title">Add New Update</h5>
                                    <button type="button" className="btn-close" onClick={() => setShowAddUpdateModal(false)}></button>
                                </div>
                                <div className="modal-body" style={{ padding: '1.5rem' }}>
                                    <form>
                                        <div className="mb-3">
                                            <label className="form-label">Date</label>
                                            <input
                                                type="date"
                                                className="form-control"
                                                value={newUpdate.updateDate}
                                                onChange={(e) => setNewUpdate({
                                                    ...newUpdate,
                                                    updateDate: e.target.value
                                                })}
                                            />
                                        </div>
                                        <div className="mb-3">
                                            <label className="form-label">Update Content</label>
                                            <div className="position-relative">
                                                <ReactQuill
                                                    theme="snow"
                                                    value={newUpdate.update}
                                                    onChange={(content) => setNewUpdate({
                                                        ...newUpdate,
                                                        update: content
                                                    })}
                                                    formats={formats}
                                                    modules={modules}
                                                    style={{ height: '320px', marginBottom: '20px' }}
                                                />
                                                <div className={`word-count ${countWords(newUpdate.update) > 150 ? 'text-danger' : 'text-muted'}`} style={{ fontSize: '0.875rem', marginTop: '0.5rem', textAlign: 'right' }}>
                                                    {countWords(newUpdate.update)}/150 words
                                                </div>
                                            </div>
                                        </div>
                                        {/* <div className="mb-3">
                                            <label className="form-label">Updated By</label>
                                            <input
                                                type="text"
                                                className="form-control"
                                                value={newUpdate.updatedBy}
                                                onChange={(e) => setNewUpdate({
                                                    ...newUpdate,
                                                    updatedBy: e.target.value
                                                })}
                                            />
                                        </div> */}
                                    </form>
                                </div>
                                <div className="modal-footer">
                                    <button 
                                        type="button" 
                                        className="btn btn-secondary" 
                                        onClick={() => setShowAddUpdateModal(false)}
                                    >
                                        Cancel
                                    </button>
                                    <button 
                                        type="button" 
                                        className="btn btn-primary" 
                                        onClick={handleSaveUpdate}
                                    >
                                        Save Update
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </>
            )}

            {showEditUpdateModal && (
                <>
                    <div className="modal-backdrop show" onClick={() => setShowEditUpdateModal(false)}></div>
                    <div className="modal d-block show" tabIndex="-1" role="dialog">
                        <div className="modal-dialog modal-lg modal-dialog-centered" onClick={e => e.stopPropagation()}>
                            <div className="modal-content" style={{ maxHeight: '75vh', overflowY: 'auto' }}>
                                <div className="modal-header">
                                    <h5 className="modal-title">Edit Update</h5>
                                    <button type="button" className="btn-close" onClick={() => setShowEditUpdateModal(false)}></button>
                                </div>
                                <div className="modal-body" style={{ padding: '1.5rem' }}>
                                    <form>
                                        <div className="mb-3">
                                            <label className="form-label">Date</label>
                                            <input
                                                type="date"
                                                className="form-control"
                                                value={editingUpdate.updateDate}
                                                onChange={(e) => setEditingUpdate({
                                                    ...editingUpdate,
                                                    updateDate: e.target.value
                                                })}
                                            />
                                        </div>
                                        <div className="mb-3">
                                            <label className="form-label">Update Content</label>
                                            <div className="position-relative">
                                                <ReactQuill
                                                    theme="snow"
                                                    value={editingUpdate.update}
                                                    onChange={(content) => setEditingUpdate({
                                                        ...editingUpdate,
                                                        update: content
                                                    })}
                                                    formats={formats}
                                                    modules={modules}
                                                    style={{ height: '200px', marginBottom: '20px' }}
                                                />
                                                <div className={`word-count ${countWords(editingUpdate.update) > 150 ? 'text-danger' : 'text-muted'}`} style={{ fontSize: '0.875rem', marginTop: '0.5rem', textAlign: 'right' }}>
                                                    {countWords(editingUpdate.update)}/150 words
                                                </div>
                                            </div>
                                        </div>
                                        <div className="mb-3">
                                            <label className="form-label">Updated By</label>
                                            <input
                                                type="text"
                                                className="form-control"
                                                value={editingUpdate.updatedBy}
                                                onChange={(e) => setEditingUpdate({
                                                    ...editingUpdate,
                                                    updatedBy: e.target.value
                                                })}
                                            />
                                        </div>
                                    </form>
                                </div>
                                <div className="modal-footer">
                                    <button 
                                        type="button" 
                                        className="btn btn-secondary" 
                                        onClick={() => {
                                            setEditingUpdate(null);
                                            setShowEditUpdateModal(false);
                                        }}
                                    >
                                        Cancel
                                    </button>
                                    <button 
                                        type="button" 
                                        className="btn btn-primary" 
                                        onClick={handleEditUpdate}
                                    >
                                        Save Changes
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </>
            )}

            {showAddObjectiveModal && (
                <>
                    <div className="modal-backdrop show" onClick={() => setShowAddObjectiveModal(false)}></div>
                    <div className="modal d-block show" tabIndex="-1" role="dialog">
                        <div className="modal-dialog modal-lg modal-dialog-centered" onClick={e => e.stopPropagation()}>
                            <div className="modal-content">
                                <div className="modal-header">
                                    <h5 className="modal-title">Add New Objective</h5>
                                    <button type="button" className="btn-close" onClick={() => setShowAddObjectiveModal(false)}></button>
                                </div>
                                <div className="modal-body" style={{ padding: '1.5rem' }}>
                                    <form>
                                        <div className="mb-3">
                                            <label className="form-label">Objective</label>
                                            <textarea
                                                className="form-control"
                                                value={newObjective}
                                                onChange={(e) => setNewObjective(e.target.value)}
                                                style={{ height: '200px', resize: 'vertical' }}
                                                placeholder="Enter the objective description..."
                                            />
                                        </div>
                                    </form>
                                </div>
                                <div className="modal-footer">
                                    <button 
                                        type="button" 
                                        className="btn btn-secondary" 
                                        onClick={() => setShowAddObjectiveModal(false)}
                                    >
                                        Cancel
                                    </button>
                                    <button 
                                        type="button" 
                                        className="btn btn-primary" 
                                        onClick={handleSaveObjective}
                                    >
                                        Save Objective
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </>
            )}

            <ToastContainer 
                position="top-right"
                autoClose={3000}
                hideProgressBar={false}
                newestOnTop
                closeOnClick
                pauseOnHover
                theme="light"
            />

            {selectedDocument && (
                <DocumentDetailsModal
                    document={selectedDocument}
                    onClose={() => setSelectedDocument(null)}
                    onEdit={(updatedDoc) => {
                        const updatedDocs = documents.documents.map(doc => 
                            doc.id === updatedDoc.id ? updatedDoc : doc
                        );
                        setDocuments({ ...documents, documents: updatedDocs });
                        setSelectedDocument(updatedDoc);
                    }}
                />
            )}
        </div>
    );
};

WorkingGroupDetailsModal.propTypes = {
    workingGroup: PropTypes.shape({
        WorkingGroupName: PropTypes.string.isRequired,
        WorkingGroupType: PropTypes.string,
        SteeringGroupName: PropTypes.string,
        SteeringGroupType: PropTypes.string,
        StartDate0: PropTypes.string,
        EndDate0: PropTypes.string,
        PublishFlag: PropTypes.bool,
        Created: PropTypes.string,
        Modified: PropTypes.string,
        id: PropTypes.string
    }).isRequired,
    onHide: PropTypes.func.isRequired,
    onUpdate: PropTypes.func
};

export default WorkingGroupDetailsModal; 